// Session state machine for the panel: first-run setup → login → MFA → authenticated.
// The session itself is an HttpOnly cookie managed by the API; nothing is stored here.
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { isApiError } from "../api/client.ts";
import { getHealth, getMe, logout as apiLogout } from "../api/endpoints.ts";
import type { Me, MfaEnrollment } from "../api/types.ts";

export type SessionStatus = "loading" | "setup_required" | "signed_out" | "mfa_enrollment" | "mfa_verify" | "authenticated" | "api_unreachable";

interface SessionValue {
  status: SessionStatus;
  me: Me | null;
  error: unknown;
  refresh: () => Promise<void>;
  setStatus: (s: SessionStatus) => void;
  signOut: () => Promise<void>;
  /** TOTP enrollment returned by first-run setup, kept in memory only until confirmed. */
  pendingEnrollment: MfaEnrollment | null;
  setPendingEnrollment: (e: MfaEnrollment | null) => void;
}

const SessionContext = createContext<SessionValue | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<SessionStatus>("loading");
  const [me, setMe] = useState<Me | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [pendingEnrollment, setPendingEnrollment] = useState<MfaEnrollment | null>(null);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const current = await getMe();
      setMe(current);
      // Admin and maintainer sessions must have MFA enrolled and verified (API: mfa.enrollment_required).
      if (current.mfa.enrollment_required && !current.mfa.enabled) setStatus("mfa_enrollment");
      else if (current.mfa.enrollment_required) setStatus("mfa_verify");
      else setStatus("authenticated");
      return;
    } catch (e) {
      setMe(null);
      if (isApiError(e) && e.code === "mfa_required") {
        const enrollment = e.details?.["enrollment_required"] === true;
        setStatus(enrollment ? "mfa_enrollment" : "mfa_verify");
        return;
      }
      if (isApiError(e) && (e.status === 401 || e.status === 403)) {
        // Distinguish first run from a normal signed-out state via health.
        try {
          const health = await getHealth();
          setStatus(health.setup_required === true ? "setup_required" : "signed_out");
        } catch {
          setStatus("signed_out");
        }
        return;
      }
      setError(e);
      setStatus("api_unreachable");
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const signOut = useCallback(async () => {
    try {
      await apiLogout();
    } finally {
      setMe(null);
      setPendingEnrollment(null);
      setStatus("signed_out");
    }
  }, []);

  const value = useMemo(
    () => ({ status, me, error, refresh, setStatus, signOut, pendingEnrollment, setPendingEnrollment }),
    [status, me, error, refresh, signOut, pendingEnrollment],
  );
  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionValue {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error("useSession must be used inside SessionProvider");
  return ctx;
}
